import { type ReactNode } from "react";

import {
  Switch as ReactAriaSwitch,
  type SwitchProps as ReactAriaSwitchProps,
  composeRenderProps,
} from "react-aria-components";

import { useUIVersion } from "#hooks";
import { customTv } from "#utils";

import { circleStyles, containerStyles } from "./Switch.styles";

const switchStyles = customTv({
  // `relative` is required so the visually-hidden <input> rendered by
  // react-aria-components (which uses `position: absolute`) is positioned
  // relative to this label rather than the document root. Without it,
  // clicking the switch can scroll the page out of view when the
  // switch is nested inside a scrollable container (e.g. a table).
  base: "relative group flex items-center gap-md text-label-medium font-medium transition text-text-primary",
  variants: {
    isDisabled: {
      true: "text-text-disabled cursor-not-allowed",
    },
    uiVersion: {
      v1: "text-[10px] uppercase",
      v2: "",
    },
  },
});

export interface SwitchProps extends ReactAriaSwitchProps {
  children?: ReactNode;
  className?: string;
  position?: "leading" | "trailing";
}

export function Switch({ children, position = "leading", className, ...props }: SwitchProps) {
  const uiVersion = useUIVersion();

  return (
    <ReactAriaSwitch
      className={composeRenderProps(className, (className, renderProps) =>
        switchStyles({ ...renderProps, className, uiVersion }),
      )}
      {...props}
    >
      {({ isSelected, isHovered, isFocused, isDisabled }) => (
        <>
          {position === "leading" ? children : null}
          <div
            className={containerStyles({
              isSelected,
              isHovered,
              isFocused,
              isDisabled,
              uiVersion,
            })}
          >
            <div className={circleStyles({ isSelected, isDisabled, uiVersion })} />
          </div>
          {position === "trailing" ? <span>{children}</span> : null}
        </>
      )}
    </ReactAriaSwitch>
  );
}
